import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { sanitizeRichtextHtml } from '../../sanitize/sanitizeRichtextHtml.js';
import { inlineHeadingHtml } from '../../sanitize/inlineHeadingHtml.js';
import { lineHeightField, marginFields, visibilityField, weightField, widthField, } from '../_shared/blockFields.js';
import { marginStyle, num, readLineHeight, readWeight, str, visibilityCss, widthCss, } from '../_shared/blockRender.js';
import { animationFields, blinkTextCss, readBlinkSpeed, readTextAnimation, textAnimationAttrs, useTextAnimation, } from '../_shared/textAnimation.js';
import { isThemeColorToken } from '../../theme/colorTokens.js';
import { blockGradientCss, blockGradientField } from '../../sections/_shared/textColor.js';
/**
 * Shrine `heading` generic content block — config-by-config parity. A standalone styled heading the
 * merchant types (Shrine `inline_richtext` "title"), with a SIZE tier (Extra small / Small / Medium /
 * Large / Custom → custom desktop+mobile px), independent DESKTOP and MOBILE alignment, an optional
 * custom text colour, AND Shrine's two "Highlight" subsystems that style BOLD (Highlight 1) and ITALIC
 * (Highlight 2) words inside the title. Responsive (Shrine breakpoint 750px) via BOTH `@media` and
 * `@container aqx-page` so the builder device-preview reflows too — mirroring the `title`/`text` blocks.
 *
 * SIZE → font-size: Shrine maps the size tier to its global heading scale (h3<h2<h1<h0). We reproduce the
 * tier ladder with concrete desktop/mobile px pairs (h3 = Extra small … h0 = Large), and the ELEMENT TAG
 * tracks the tier (h3/h2 for the small tiers, h2 for the large tiers — h0/h1 are visual scale classes in
 * Shrine, not page-level h1s, so we keep the rendered tag at h2/h3 to stay a generic *content* heading
 * and never collide with the product title's h1). "Custom" uses the merchant's px values directly.
 *
 * HIGHLIGHT 1 (bold words `<strong>`/`<b>`) and HIGHLIGHT 2 (italic words `<em>`/`<i>`) each offer the
 * full Shrine style menu: solid color, gradient color (clipped to the glyphs), background pill (radius +
 * padding + optional border), underline (solid or gradient, with thickness + vertical position), and the
 * three "handwritten" hand-drawn line strokes (line-1/2/3) reproduced as inline-SVG underline overlays
 * (color + stroke-width + vertical position). Highlight 2 additionally exposes "keep italic" to preserve
 * the native italic slant while restyling.
 *
 * ANIMATIONS: the Shrine `text_animation` group is exposed (none / split / shiny / blink / blur / typing).
 * "Shiny" is a pure-CSS continuous shimmer and renders here now (wired by `shinySpeed`); "Blink" is the
 * same kind of thing (a keyframed opacity, `blinkSpeed`), from the shared helper. split / blur /
 * typing are JS reveal effects (per-char/word DOM splitting + IntersectionObserver scroll trigger + the
 * typewriter loop) that activate via the shared page animation runtime — NOT yet wired (the static heading
 * always renders correctly without them).
 *
 * DEFERRED (Shrine settings intentionally NOT reproduced):
 *  - The highlight-animation knobs (highlight-swipe / fade-up / underline / strike + their *_color /
 *    *_thickness / *_duration / *_delay / *_on_scroll fields) — JS reveal effects, part of the runtime.
 *  - Shrine's `alignment` / `mobile_alignment` / `margin_bottom` `visible_if` clauses that reference
 *    SECTION-level settings (parent section content-alignment + an image_1 setting) — those parent
 *    settings only exist in the Shopify section context; as a generic block we always expose the controls.
 */
/** The Shrine highlight-style menu, declared once for Highlight 1 (no "none") and Highlight 2 (with "none"). */
const HIGHLIGHT_STYLE_OPTIONS = [
    { value: 'solid-color', label: 'Color' },
    { value: 'gradient-color', label: 'Gradient' },
    { value: 'bg-color', label: 'Background' },
    { value: 'underline-solid', label: 'Underline' },
    { value: 'underline-gradient', label: 'Gradient underline' },
    { value: 'line-1', label: 'Hand line 1' },
    { value: 'line-2', label: 'Hand line 2' },
    { value: 'line-3', label: 'Hand line 3' },
];
const DEFAULT_GRADIENT = 'linear-gradient(57deg, rgba(249, 119, 255, 1) 8%, rgba(54, 212, 255, 1) 47%, rgba(43, 252, 135, 1) 83%)';
/**
 * Build the full Shrine "Highlight" field-set for one slot. `n` is 1 (bold) or 2 (italic); ids are
 * camelCased + slot-numbered (`hl1Style`, `hl1SolidColor`, …) so the two slots never collide. Highlight 2
 * leads with a "none" option + a "keep italic" toggle; Highlight 1 is always on (defaults to solid).
 */
function highlightFields(n) {
    const g = `Highlight ${n}`;
    const p = `hl${n}`;
    const styleField = `${p}Style`;
    const styleOptions = n === 2
        ? [{ value: 'none', label: 'None' }, ...HIGHLIGHT_STYLE_OPTIONS]
        : HIGHLIGHT_STYLE_OPTIONS;
    const fields = [
        {
            id: styleField,
            type: 'select',
            label: n === 1 ? 'Bold text style' : 'Italic text style',
            group: g,
            info: n === 1
                ? 'Put words in BOLD to highlight them with this style.'
                : 'Put words in ITALIC to highlight them with this style. Keep it as "None" to use the default italic style.',
            default: n === 1 ? 'solid-color' : 'none',
            options: styleOptions,
        },
    ];
    if (n === 2) {
        fields.push({
            id: `${p}KeepItalic`,
            type: 'checkbox',
            label: 'Keep italic',
            group: g,
            info: 'Preserve the native italic slant while applying the highlight style.',
            default: false,
            visibleWhen: { field: styleField, notEquals: 'none' },
        });
    }
    // ── gradient text ──
    fields.push({
        id: `${p}Gradient`,
        type: 'gradient',
        label: 'Gradient',
        group: g,
        default: DEFAULT_GRADIENT,
        visibleWhen: { field: styleField, equals: 'gradient-color' },
    });
    // ── solid / bg text color ──
    fields.push({
        id: `${p}Color`,
        type: 'color',
        label: 'Text color',
        group: g,
        default: { ref: 'theme', token: 'accent' },
        visibleWhen: { field: styleField, equals: ['solid-color', 'bg-color'] },
    });
    // ── background pill ──
    fields.push({
        id: `${p}BgColor`,
        type: 'color',
        label: 'Background color',
        group: g,
        default: { ref: 'theme', token: 'accent-2' },
        visibleWhen: { field: styleField, equals: 'bg-color' },
    }, {
        id: `${p}BorderRadius`,
        type: 'range',
        label: 'Border radius',
        group: g,
        min: 0,
        max: 40,
        step: 2,
        default: 8,
        unit: 'px',
        visibleWhen: { field: styleField, equals: 'bg-color' },
    }, {
        id: `${p}BorderWidth`,
        type: 'range',
        label: 'Border thickness',
        group: g,
        min: 0,
        max: 6,
        step: 1,
        default: 0,
        unit: 'px',
        visibleWhen: { field: styleField, equals: 'bg-color' },
    }, {
        id: `${p}BorderStyle`,
        type: 'select',
        label: 'Border style',
        group: g,
        default: 'solid',
        options: [
            { value: 'solid', label: 'Solid' },
            { value: 'dashed', label: 'Dashed' },
        ],
        // Shrine reveals this only when border thickness > 0; we approximate with the bg-color gate
        // (a 0-width border renders nothing regardless, so the control is harmless when width is 0).
        visibleWhen: { field: styleField, equals: 'bg-color' },
    }, {
        id: `${p}BorderColor`,
        type: 'color',
        label: 'Border color',
        group: g,
        default: { ref: 'theme', token: 'accent' },
        visibleWhen: { field: styleField, equals: 'bg-color' },
    }, {
        id: `${p}PaddingBlock`,
        type: 'range',
        label: 'Vertical padding',
        group: g,
        min: 0,
        max: 1,
        step: 0.1,
        default: 0.3,
        unit: 'em',
        visibleWhen: { field: styleField, equals: 'bg-color' },
    }, {
        id: `${p}PaddingInline`,
        type: 'range',
        label: 'Horizontal padding',
        group: g,
        min: 0,
        max: 1,
        step: 0.1,
        default: 0.3,
        unit: 'em',
        visibleWhen: { field: styleField, equals: 'bg-color' },
    });
    // ── underline (solid + gradient) ──
    fields.push({
        id: `${p}UnderlineThickness`,
        type: 'range',
        label: 'Underline thickness',
        group: g,
        min: 0,
        max: 6,
        step: 1,
        default: 2,
        unit: 'px',
        visibleWhen: { field: styleField, equals: ['underline-solid', 'underline-gradient'] },
    }, {
        id: `${p}UnderlineColor`,
        type: 'color',
        label: 'Underline color',
        group: g,
        default: { ref: 'theme', token: 'accent' },
        visibleWhen: { field: styleField, equals: 'underline-solid' },
    }, {
        id: `${p}UnderlineGradient`,
        type: 'gradient',
        label: 'Underline gradient',
        group: g,
        default: DEFAULT_GRADIENT,
        visibleWhen: { field: styleField, equals: 'underline-gradient' },
    }, {
        id: `${p}UnderlinePosition`,
        type: 'range',
        label: 'Underline position',
        group: g,
        min: 0,
        max: 100,
        step: 5,
        default: 25,
        unit: '%',
        visibleWhen: { field: styleField, equals: ['underline-solid', 'underline-gradient'] },
    });
    // ── handwritten lines (line-1/2/3) ──
    fields.push({
        id: `${p}HandColor`,
        type: 'color',
        label: 'Line color',
        group: g,
        default: { ref: 'theme', token: 'accent' },
        visibleWhen: { field: styleField, equals: ['line-1', 'line-2', 'line-3'] },
    }, {
        id: `${p}HandStrokeWidth`,
        type: 'range',
        label: 'Line thickness',
        group: g,
        min: 1,
        max: 10,
        step: 1,
        default: 3,
        unit: 'px',
        visibleWhen: { field: styleField, equals: ['line-1', 'line-2', 'line-3'] },
    }, {
        id: `${p}HandPosition`,
        type: 'range',
        label: 'Line position',
        group: g,
        min: 0,
        max: 100,
        step: 5,
        default: 60,
        unit: '%',
        visibleWhen: { field: styleField, equals: ['line-1', 'line-2', 'line-3'] },
    });
    return fields;
}
export const headingBlockSchema = {
    type: 'heading',
    name: 'Heading',
    fields: [
        visibilityField,
        {
            id: 'title',
            type: 'richtext',
            label: 'Heading',
            default: { format: 'html', html: 'Parlez de votre marque' },
        },
        weightField,
        widthField,
        {
            id: 'headingSize',
            aiConfigurable: true,
            type: 'select',
            label: 'Heading size',
            default: 'h1',
            options: [
                { value: 'h3', label: 'Extra small' },
                { value: 'h2', label: 'Small' },
                { value: 'h1', label: 'Medium' },
                { value: 'h0', label: 'Large' },
                { value: 'custom', label: 'Custom' },
            ],
        },
        {
            id: 'customDesktopSize',
            type: 'number',
            label: 'Custom desktop size',
            default: 39,
            unit: 'px',
            visibleWhen: { field: 'headingSize', equals: 'custom' },
        },
        {
            id: 'customMobileSize',
            type: 'number',
            label: 'Custom mobile size',
            default: 28,
            unit: 'px',
            visibleWhen: { field: 'headingSize', equals: 'custom' },
        },
        // Beside the sizes, for the reason the `text` block grew one: the theme leading is a RATIO, so the
        // bigger the tier the wider the hole it opens at a wrap.
        lineHeightField(1.3),
        {
            id: 'alignment',
            aiConfigurable: true,
            type: 'select',
            label: 'Desktop alignment',
            default: 'left',
            options: [
                { value: 'left', label: 'Left' },
                { value: 'center', label: 'Center' },
                { value: 'right', label: 'Right' },
            ],
        },
        {
            id: 'mobileAlignment',
            type: 'select',
            label: 'Mobile alignment',
            group: 'Mobile',
            default: 'mobile-left',
            options: [
                { value: 'mobile-left', label: 'Left' },
                { value: 'mobile-center', label: 'Center' },
                { value: 'mobile-right', label: 'Right' },
            ],
        },
        {
            id: 'enableCustomColor',
            type: 'checkbox',
            label: 'Enable custom text color',
            group: 'Color',
            default: false,
        },
        {
            id: 'customColor',
            type: 'color',
            label: 'Custom text color',
            group: 'Color',
            default: { ref: 'custom', value: '#FF0000' },
            visibleWhen: { field: 'enableCustomColor', equals: true },
        },
        // The gradient sits beside the colour, behind the same switch (2026-09-20). In the Rich text section
        // the heading IS this block, so the section's own "Text colors" group is not what the seller has open
        // when she asks for a gradient title — this is.
        blockGradientField('Color'),
        // ── Highlight 1 (bold words) + Highlight 2 (italic words) ──
        ...highlightFields(1),
        ...highlightFields(2),
        // ── Animations (Shrine `text_animation`) — shared runtime: shiny (CSS), split / blur / typing (JS
        //    reveal on scroll via the useTextAnimation hook). ───────────────────────────────────────────────
        ...animationFields(),
        ...marginFields(15, 15),
    ],
};
/**
 * Size tier → [desktopPx, mobilePx] + rendered element tag. h0 (Large) / h1 (Medium) render as an `h2`
 * element (visual scale only, never a page h1); h2 (Small) → `h2`; h3 (Extra small) → `h3`.
 */
const SIZE_TIERS = {
    h3: { desktop: 22, mobile: 18, tag: 'h3' },
    h2: { desktop: 28, mobile: 22, tag: 'h2' },
    h1: { desktop: 39, mobile: 28, tag: 'h2' },
    h0: { desktop: 52, mobile: 34, tag: 'h2' },
};
/** Map Shrine's `mobile-left|center|right` (and bare `left|center|right`) to a CSS text-align keyword. */
function alignValue(v, fallback) {
    const s = str(v, fallback).replace('mobile-', '');
    return s === 'center' || s === 'right' ? s : 'left';
}
/**
 * A stored `color` setting is a ColorValue; resolve it to a CSS colour. Custom → its hex; a theme token
 * → `var(--aqx-theme-<token>)` so a re-theme reflows (0019 §4a — the private-resolver theme branch,
 * allowlisted per finding #18); else a literal / the fallback.
 */
function colorHex(v, fallback) {
    if (v && typeof v === 'object') {
        const c = v;
        if (c.ref === 'custom' && typeof c.value === 'string')
            return c.value;
        if (c.ref === 'theme' && typeof c.token === 'string' && isThemeColorToken(c.token))
            return `var(--aqx-theme-${c.token})`;
    }
    if (typeof v === 'string' && v.length > 0)
        return v;
    return fallback;
}
/**
 * Inline-SVG hand-drawn underline data-URIs for line-1/2/3. Each is a single wavy/slashed stroke drawn in
 * a 0–100 viewBox; we recolor via `stroke` and size the stroke via `stroke-width`. The SVG is injected as
 * a CSS `background-image` behind the highlighted run (Shrine's "handwritten" overlay, reproduced without
 * the Shopify snippet). `currentColor` lets the surrounding `color` drive the stroke.
 */
function handLineSvg(kind, color, strokeWidth) {
    const paths = {
        // straight-ish hand-drawn underline
        'line-1': 'M2,8 C20,4 40,12 60,7 C78,3 92,9 98,6',
        // double-pass scribble
        'line-2': 'M2,7 C25,2 55,12 98,5 M3,10 C30,6 60,14 97,9',
        // diagonal slash highlight
        'line-3': 'M2,12 C30,9 50,4 98,3',
    };
    const d = paths[kind];
    const svg = `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 14' preserveAspectRatio='none'>` +
        `<path d='${d}' fill='none' stroke='${color}' stroke-width='${strokeWidth}' ` +
        `stroke-linecap='round' stroke-linejoin='round'/></svg>`;
    return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
}
/**
 * Build the scoped CSS for one highlight slot. `sel` targets the matched inline tags (bold or italic) under
 * the heading. Returns '' when the style is "none". `removeNativeItalic` (slot 2 only) controls whether the
 * native italic slant is dropped (Shrine's "keep italic" inverted).
 */
// The fallbacks below are the SAME theme tokens the schema declares as defaults (`{ref:'theme',token:'accent'}` /
// `accent-2`). They used to be Shrine's demo blue `#2F5F8A` / `#D2EBFF`: a block whose colour had never been
// touched then showed « Accent 1 » in the inspector and rendered a blue the seller had replaced months ago
// (founder, 2026-09-11, store novendra). Inspector and canvas must read the same default.
function highlightCss(bs, n, sel, 
/** Whether to strip the native italic slant (slot 2; slot 1 never has one). */
removeNativeItalic) {
    const p = `hl${n}`;
    const style = str(bs[`${p}Style`], n === 1 ? 'solid-color' : 'none');
    if (style === 'none')
        return '';
    // Slot 2 keeps the native italic only when "keep italic" is on; slot 1 has no italic to preserve.
    const slant = n === 2 && removeNativeItalic ? 'font-style:normal;' : '';
    if (style === 'solid-color') {
        const color = colorHex(bs[`${p}Color`], 'var(--aqx-theme-accent)');
        return `${sel}{color:${color};${slant}}`;
    }
    if (style === 'gradient-color') {
        const grad = str(bs[`${p}Gradient`], DEFAULT_GRADIENT);
        return (`${sel}{background:${grad};-webkit-background-clip:text;background-clip:text;` +
            `-webkit-text-fill-color:transparent;color:transparent;${slant}}`);
    }
    if (style === 'bg-color') {
        const bg = colorHex(bs[`${p}BgColor`], 'var(--aqx-theme-accent-2)');
        const txt = colorHex(bs[`${p}Color`], 'var(--aqx-theme-accent)');
        const radius = num(bs[`${p}BorderRadius`], 8);
        const borderWidth = num(bs[`${p}BorderWidth`], 0);
        const borderStyleVal = str(bs[`${p}BorderStyle`], 'solid');
        const borderColor = colorHex(bs[`${p}BorderColor`], 'var(--aqx-theme-accent)');
        const padBlock = num(bs[`${p}PaddingBlock`], 0.3);
        const padInline = num(bs[`${p}PaddingInline`], 0.3);
        const border = borderWidth > 0 ? `border:${borderWidth}px ${borderStyleVal} ${borderColor};` : '';
        return (`${sel}{background:${bg};color:${txt};` +
            `padding:${padBlock}em ${padInline}em;border-radius:${radius}px;` +
            `box-decoration-break:clone;-webkit-box-decoration-break:clone;${border}${slant}}`);
    }
    if (style === 'underline-solid' || style === 'underline-gradient') {
        const thickness = num(bs[`${p}UnderlineThickness`], 2);
        // position: 0% = sits high through the text, 100% = drops below the baseline.
        const position = num(bs[`${p}UnderlinePosition`], 25);
        const bottom = `${(100 - position) * 0.01 * 0.4}em`;
        const stroke = style === 'underline-gradient'
            ? str(bs[`${p}UnderlineGradient`], DEFAULT_GRADIENT)
            : colorHex(bs[`${p}UnderlineColor`], 'var(--aqx-theme-accent)');
        // A linear-gradient background sized as a thin band → works for both a flat color and a real gradient.
        const bgImage = style === 'underline-gradient' ? stroke : `linear-gradient(${stroke},${stroke})`;
        return (`${sel}{background-image:${bgImage};background-repeat:no-repeat;` +
            `background-size:100% ${thickness}px;background-position:0 calc(100% - ${bottom});` +
            `padding-bottom:0.1em;${slant}}`);
    }
    // handwritten line-1/2/3 → inline-SVG underline overlay.
    if (style === 'line-1' || style === 'line-2' || style === 'line-3') {
        const color = colorHex(bs[`${p}HandColor`], 'var(--aqx-theme-accent)');
        const strokeWidth = num(bs[`${p}HandStrokeWidth`], 3);
        const position = num(bs[`${p}HandPosition`], 60);
        const bottom = `${(100 - position) * 0.01 * 0.45}em`;
        const img = handLineSvg(style, color, strokeWidth);
        return (`${sel}{background-image:${img};background-repeat:no-repeat;` +
            `background-size:100% 0.5em;background-position:0 calc(100% - ${bottom});` +
            `padding-bottom:0.15em;${slant}}`);
    }
    return '';
}
/**
 * Heading content block — independent desktop/mobile font-size + alignment + the two Highlight subsystems,
 * all via a per-block scoped style. Mobile-first base, desktop override after (matches the `title` block's
 * responsive pattern).
 */
const HeadingBlock = ({ block }) => {
    const bs = block.settings;
    const cls = `aqx-hd-${block.id}`;
    // Reveal animation (split / blur / typing) on scroll-into-view; shiny is handled by the scoped CSS below.
    const textAnim = readTextAnimation(bs);
    const animRef = useTextAnimation(textAnim);
    const titleVal = bs.title;
    // The value goes INSIDE an <h2>/<h3>, so it must be phrasing content: a seller who pressed the
    // editor's H2 button saved a nested heading, which the document parser ejects on the live shop —
    // see inlineHeadingHtml. Flattening here keeps the builder and the storefront on the same size.
    const html = inlineHeadingHtml(sanitizeRichtextHtml(titleVal?.format === 'html' ? titleVal.html : ''));
    const sizeKey = str(bs.headingSize, 'h1');
    const isCustom = sizeKey === 'custom';
    const tier = SIZE_TIERS[sizeKey] ?? { desktop: 39, mobile: 28, tag: 'h2' };
    const deskSize = isCustom ? num(bs.customDesktopSize, 39) : tier.desktop;
    const mobSize = isCustom ? num(bs.customMobileSize, 28) : tier.mobile;
    const Tag = isCustom ? 'h2' : tier.tag;
    const deskAlign = alignValue(bs.alignment, 'left');
    const mobAlign = alignValue(bs.mobileAlignment, 'mobile-left');
    // Shared "Font weight" override; '' (unset) keeps the theme heading weight, so the default stays byte-identical.
    const weight = readWeight(bs);
    // Custom colour overrides the scheme text colour (Shrine: enable_custom_color + custom_color). Resolve via
    // colorHex so a theme-swatch pick ({ref:'theme',token}) maps to var(--aqx-theme-<token>) instead of being
    // dropped — matches every other colour field in this block and the sibling buy-buttons/view-details blocks.
    let color = 'var(--aqx-scheme-text)';
    if (bs.enableCustomColor === true) {
        color = colorHex(bs.customColor, 'var(--aqx-scheme-text)');
    }
    // Highlight subsystems target bold (slot 1) and italic (slot 2) runs inside the heading.
    const hl1Css = highlightCss(bs, 1, `.${cls} strong,.${cls} b`, false);
    const hl2Css = highlightCss(bs, 2, `.${cls} em,.${cls} i`, bs.hl2KeepItalic !== true);
    // "Shiny" animation — a continuous CSS shimmer sweeping a light band across the glyphs (gradient clipped
    // to text + keyframed background-position). Pure CSS, no JS/scroll observer; `shinySpeed` is the sweep
    // duration. The base text colour shows through, so it honours custom/scheme colour. Reduced-motion pins it.
    const isShiny = str(bs.textAnimation, 'none') === 'shiny';
    const shinySpeed = num(bs.shinySpeed, 5);
    const shinyCss = isShiny
        ? `@keyframes aqx-hd-shine-${block.id}{to{background-position:-200% 0}}` +
            `.${cls}{` +
            `background:linear-gradient(110deg,${color} 0%,${color} 40%,rgba(255,255,255,0.85) 50%,${color} 60%,${color} 100%);` +
            `background-size:200% 100%;-webkit-background-clip:text;background-clip:text;` +
            `-webkit-text-fill-color:transparent;color:transparent;` +
            `animation:aqx-hd-shine-${block.id} ${shinySpeed}s linear infinite}` +
            `@media(prefers-reduced-motion:reduce){.${cls}{animation:none}}`
        : '';
    // "Blink" — opacity only, so the colour, the gradient and the highlights below all stay the seller's.
    const blinkCss = textAnim.type === 'blink'
        ? blinkTextCss(`.${cls}`, `aqx-hd-blink-${block.id}`, readBlinkSpeed(bs))
        : '';
    // Mobile-first base; the desktop size/align override is keyed ONLY to `@container aqx-page`. That container
    // is always present (PageRenderer wraps the page on BOTH storefront and builder), so it reflects the true
    // content width in both. A paired `@media(min-width)` would ALSO fire off the builder's wide editor WINDOW —
    // forcing the desktop size inside a narrow mobile preview frame — so it is intentionally omitted.
    const desktopRule = `.${cls}{font-size:${deskSize}px;text-align:${deskAlign}}`;
    const css = `.${cls}{font-size:${mobSize}px;text-align:${mobAlign}}` +
        `@container aqx-page (min-width:750px){${desktopRule}}` +
        // Optional desktop width (% of the column), positioned by the desktop alignment; '' at 100%.
        widthCss(cls, bs, deskAlign) +
        hl1Css +
        hl2Css +
        shinyCss +
        blinkCss +
        // The seller's own gradient, last — but never against "shiny", which already owns the glyphs with a
        // gradient of its own (two clipped backgrounds on one element paint whichever the cascade picked, and
        // the shimmer would simply stop moving).
        (isShiny ? '' : blockGradientCss(`.${cls}`, bs)) +
        visibilityCss(cls, bs.visibility);
    const headingStyle = {
        fontFamily: 'var(--aqx-theme-heading-font, var(--aqx-theme-font))',
        // Reference Shrine store uses heading weight 600 (semibold), not 700; the shared weight field overrides it.
        fontWeight: (weight || 'var(--aqx-theme-fw-heading, 600)'),
        // 0019 §5a: single source of truth for heading leading/tracking (Shrine heading = 1.3 / 0.06em) —
        // unless the seller took the leading over on this block (`lineHeightField`, 20/09/2026).
        lineHeight: readLineHeight(bs, 'var(--aqx-theme-lh-heading, 1.3)'),
        letterSpacing: 'var(--aqx-theme-tracking-heading, 0.06em)',
        // When shiny is active the colour is owned by the scoped gradient (color:transparent); an inline color
        // would override it, so omit it in that case.
        ...(isShiny ? {} : { color }),
        wordBreak: 'break-word',
        ...marginStyle(bs, 15, 15),
    };
    return (_jsxs(_Fragment, { children: [_jsx("style", { dangerouslySetInnerHTML: { __html: css } }), _jsx(Tag, { ref: animRef, className: cls, style: headingStyle, ...textAnimationAttrs(textAnim), dangerouslySetInnerHTML: { __html: html } })] }));
};
export const headingBlock = {
    type: 'heading',
    schema: headingBlockSchema,
    Component: HeadingBlock,
    meta: { label: 'Heading' },
};
//# sourceMappingURL=index.js.map